export type FolderScope = 'user' | 'org' | 'external';

export type ResourceListColumn = {
  label: string;
  width?: string;
};

/**
 * Type size shared by every secondary column cell of a resource-list row —
 * creator, scope, timings, trigger, status. One step below the row's `text-sm`
 * name, so the name stays the thing you scan down and the columns beside it read
 * as one family rather than competing with it.
 *
 * Split out from {@link RESOURCE_LIST_CELL_TEXT} for the cells that pick their
 * own colour (a failed status goes red, a placeholder goes half-muted) and would
 * otherwise have to restate the size to do it.
 */
export const RESOURCE_LIST_CELL_TEXT_SIZE = 'text-[11px]';

/**
 * The usual secondary cell: {@link RESOURCE_LIST_CELL_TEXT_SIZE}, muted.
 *
 * These exist as constants because the size kept drifting: `ResourceCreatorCell`
 * and the scope badges were built at 11px, while every date and timing cell
 * reached for `Text.Caption`, which bakes in `text-sm` — so half the columns in a
 * row rendered at the same size as its title.
 */
export const RESOURCE_LIST_CELL_TEXT = `${RESOURCE_LIST_CELL_TEXT_SIZE} text-muted-foreground`;

/**
 * Names a metadata cell's value while the list is too narrow for columns, using
 * the same words its column header uses when there is one.
 *
 * Wrapped onto one line the values lose the thing that said what they were:
 * `You  Aug 27` is two facts and no labels, and a bare avatar and name could be
 * the person who made this or a person it went to.
 *
 * Exactly one cell uses this, and that is the point. The sharing cell had a
 * label too, and the pair of them a few pixels apart — "Created by <name>"
 * beside "Shared with <name>" — was worse than either alone: two labels of the
 * same weight, each followed by a person, and no way to tell at a glance which
 * answered which. Sharing now leads with a glyph instead (see
 * {@link ResourceScopeBadge}), which says "this is about access" without
 * competing for the same kind of attention. A second label here would bring the
 * problem straight back, so prefer a glyph for anything else this line grows.
 *
 * A `::before` rather than a label element, for one specific reason: a stacked
 * row hides cells that resolved to nothing (`[&>*:empty]:hidden` in
 * {@link ResourceListSecondaryCells}), and `:empty` ignores pseudo-elements. A
 * real `<span>` label inside the cell would keep an otherwise-empty cell alive
 * and strand "Created by" above no value at all — a folder that groups a type
 * has no creator. The pseudo-element is invisible to `:empty`, so a cell with no
 * value still collapses and takes its label with it.
 *
 * `flex` because `::before` has to sit *beside* the value; without it the cell's
 * block-level content pushes the label onto its own line.
 *
 * The label is spelled out in the constant rather than built from a parameter:
 * Tailwind only generates a `content-[…]` utility it can see written out in the
 * source.
 */
const CELL_LABEL_BASE =
  '@max-list-cols/list:flex @max-list-cols/list:items-center @max-list-cols/list:before:mr-1.5 @max-list-cols/list:before:shrink-0 @max-list-cols/list:before:text-[11px] @max-list-cols/list:before:text-muted-foreground/70';

export const CELL_LABEL_CREATED_BY = `${CELL_LABEL_BASE} @max-list-cols/list:before:content-['Created_by']`;

/**
 * Column presets shared across the resource lists.
 *
 * Every column reads from the left, header and cells alike. These used to be
 * right-aligned — the convention for a numeric column, borrowed on the grounds
 * that dates and durations are numbers. They are not read as numbers here:
 * nobody compares magnitudes down a column of "Aug 27", and the values are
 * ragged (`Everyone` beside `Private`, `Aug 5` beside `Aug 27`), so a right edge
 * bought nothing and cost the left one. With the sharing cell leading on a
 * glyph it cost something visible: the locks and globes landed at a different
 * `x` on every row instead of forming a column you can scan.
 */
export const RESOURCE_LIST_COLUMNS = {
  folder: { label: 'Folder', width: '120px' },
  scope: { label: 'Scope', width: '90px' },
  lastActive: { label: 'Last active', width: '140px' },
  nextRun: { label: 'Next run (UTC)', width: '150px' },
  /**
   * Attribution as its own column. It used to surface only as an inline "Owned
   * by" badge inside the sharing column, and only on items someone else owned —
   * so your own carried none, and there was no column to scan down for who made
   * what.
   */
  createdBy: { columnKey: 'creator', label: 'Created by', width: '140px' },
  /**
   * Every state of this column answers "who can see this" — "Everyone" for
   * org-wide, an avatar stack for named people, "Specific people" for a
   * link-only share, and a dash for private. It rendered with no header at all,
   * which left both sighted users and screen readers to infer that from the
   * badges. "Shared with" matches the wording the badge tooltips use, and 150px
   * fits "Everyone" and an avatar stack without clipping.
   */
  sharedWith: { columnKey: 'scope', label: 'Shared with', width: '150px' },
};
